// src/components/Header.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowRight,
  FaBell,
  FaClock,
  FaCreditCard,
  FaCog,
  FaImage,
  FaMoon,
  FaSun,
  FaBuilding,
  FaHome,
  FaTachometerAlt,
  FaBookOpen,
  FaUserCircle,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaHeart,
  FaCompass,
} from "react-icons/fa";
import { terminateSession } from "../utils/sessionManager";
import {
  ADMIN_ROLES,
  getDashboardPathForRole,
  MANAGEMENT_ROLES,
  normalizeRole,
  ROLE_ADMIN,
  ROLE_PRINCIPAL,
  ROLE_SCHOOL,
  ROLE_SUPER_ADMIN,
  ROLE_TEACHER,
  SCHOOL_ROLES,
  STUDENT_ROLES,
} from "../utils/authRole";
import { useAppTheme } from "../context/AppThemeContext";
import { useStudentProfile } from "../context/StudentProfileContext";
import { useTeacherProfile } from "../context/TeacherProfileContext";
import { useTenant } from "../context/TenantContext";
import { useUsageTracker } from "../context/UsageTrackerContext";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { revokeBrowserSession } from "../api/authSession";
import ClassNXLoader from "./ClassNXLoader";

const DARK_MODE_HINT_KEY = "ui:dark-mode-hint-dismissed";
const PREMIUM_PLAN_SET = new Set(["PREMIUM", "GROWTH", "ENTERPRISE", "PRO"]);
const FREE_PLAN_SET = new Set(["FREE", "TRIAL", "FREE_TRIAL"]);
const RENEWAL_NOTICE_DAYS = 7;
const CLASSNX_LOGO_SRC = `${process.env.PUBLIC_URL}/logo.png`;

const MOBILE_PAGE_LABELS = {
  "/": "Welcome",
  "/services": "Explore services",
  "/about": "About ClassNX",
  "/login": "Sign in",
  "/profile": "My profile",
  "/moments": "Moments",
  "/admin-home": "Workspace home",
  "/admin-board": "Admin workspace",
  "/teacher/home": "Teacher home",
  "/teacher-dashboard": "Teacher dashboard",
  "/user-dashboard": "Student dashboard",
  "/school-dashboard": "School dashboard",
  "/super-admin/home": "Platform home",
  "/super-admin": "Platform dashboard",
  "/principal": "Principal portal",
};

const getMobilePageLabel = (pathname = "") => {
  if (MOBILE_PAGE_LABELS[pathname]) return MOBILE_PAGE_LABELS[pathname];
  if (pathname.startsWith("/admin/")) {
    const section = pathname.split("/")[2] || "workspace";
    return section.replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
  if (pathname.startsWith("/services/")) return "Service details";
  return "ClassNX workspace";
};

const ClassNXBrandMark = ({ isDarkMode, size = "lg" }) => {
  const isSmall = size === "sm";
  const s = isSmall ? 36 : 48;
  const uid = isSmall ? "cnxS" : "cnxL";

  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <defs>
        {/* Main background gradient — navy → blue → teal */}
        <linearGradient id={`${uid}bg`} x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1e3a8a" />
          <stop offset="0.48" stopColor="#1d4ed8" />
          <stop offset="1" stopColor="#0f766e" />
        </linearGradient>
        {/* Top-half shine for depth */}
        <linearGradient id={`${uid}sh`} x1="24" y1="0" x2="24" y2="30" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" stopOpacity="0.22" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
        {/* Bottom shadow for grounding */}
        <linearGradient id={`${uid}sd`} x1="24" y1="24" x2="24" y2="48" gradientUnits="userSpaceOnUse">
          <stop stopColor="black" stopOpacity="0" />
          <stop offset="1" stopColor="black" stopOpacity="0.28" />
        </linearGradient>
        <clipPath id={`${uid}cl`}>
          <rect width="48" height="48" rx="13" />
        </clipPath>
      </defs>

      {/* Base gradient card */}
      <rect width="48" height="48" rx="13" fill={`url(#${uid}bg)`} />
      {/* Shine overlay */}
      <rect width="48" height="48" rx="13" fill={`url(#${uid}sh)`} clipPath={`url(#${uid}cl)`} />
      {/* Shadow overlay */}
      <rect width="48" height="48" rx="13" fill={`url(#${uid}sd)`} clipPath={`url(#${uid}cl)`} />
      {/* Hairline inner border */}
      <rect x="1.5" y="1.5" width="45" height="45" rx="11.5" stroke="white" strokeOpacity="0.16" strokeWidth="1" fill="none" />

      {/* Geometric N — left vertical bar */}
      <rect x="10" y="13" width="4.5" height="22" rx="1.5" fill="white" />
      {/* Geometric N — right vertical bar */}
      <rect x="33.5" y="13" width="4.5" height="22" rx="1.5" fill="white" />
      {/* Geometric N — diagonal stroke (polygon) */}
      <polygon points="14.5,13 19.5,13 33.5,35 28.5,35" fill="white" opacity="0.88" />

      {/* X accent — two crossing strokes, top-right corner */}
      <line x1="37.5" y1="9.5" x2="43.5" y2="15.5" stroke="#2dd4bf" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="43.5" y1="9.5" x2="37.5" y2="15.5" stroke="#2dd4bf" strokeWidth="2.4" strokeLinecap="round" />

      {/* Bottom pulse bar */}
      <rect x="10" y="39.5" width="28" height="2" rx="1" fill="white" fillOpacity="0.2" />
      <rect x="10" y="39.5" width="10" height="2" rx="1" fill="#2dd4bf" fillOpacity="0.7" />
    </svg>
  );
};

const Header = ({ userRole, setUserRole, hasAdminSidebar = false }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logoutOverlayRef = useRef(null);
  const { isDarkMode, toggleTheme } = useAppTheme();
  const { getCurrentSessionTime, getSessionDuration } = useUsageTracker();
  const [sessionDisplay, setSessionDisplay] = useState("");
  useEffect(() => {
    const tick = () => setSessionDisplay(getSessionDuration(getCurrentSessionTime()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [getCurrentSessionTime, getSessionDuration]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [logoDraftUrl, setLogoDraftUrl] = useState("");
  const [privateLogoUrl, setPrivateLogoUrl] = useState("");
  const privateLogoUrlRef = useRef("");
  const [showDarkModeHint, setShowDarkModeHint] = useState(false);
  const notifCount = 0;
  const settingsMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { studentProfile } = useStudentProfile();
  const { teacherProfile } = useTeacherProfile();
  const {
    activeTenant,
    tenantCatalog,
    setActiveTenantById,
    subscriptionLoading,
    subscriptionReady,
    subscriptionError,
    isSubdomainTenant,
    tenantLogoUrl,
    schoolName: contextSchoolName,
  } = useTenant();
  const role = normalizeRole(userRole);
  const isLoggedIn = !!role;
  const isSuperAdminRole = role === ROLE_SUPER_ADMIN;
  const isStudentRole = STUDENT_ROLES.includes(role);
  const isTeacherRole = role === ROLE_TEACHER;
  const isPrincipalRole = role === ROLE_PRINCIPAL;
  const isAdminRole = ADMIN_ROLES.includes(role);

  useEffect(() => {
    const handleLogoutProgress = (event) => setIsLoggingOut(event?.detail?.pending === true);
    window.addEventListener("auth:logout-progress", handleLogoutProgress);
    return () => window.removeEventListener("auth:logout-progress", handleLogoutProgress);
  }, []);

  useEffect(() => {
    if (!isLoggingOut) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    logoutOverlayRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isLoggingOut]);

  useEffect(() => {
    if (isDarkMode || !isLoggedIn) {
      setShowDarkModeHint(false);
      return;
    }

    try {
      const dismissed = localStorage.getItem(DARK_MODE_HINT_KEY) === "true";
      setShowDarkModeHint(!dismissed);
    } catch {
      setShowDarkModeHint(true);
    }
  }, [isDarkMode, isLoggedIn]);

  const isSubdomain = Boolean(isSubdomainTenant || activeTenant?.subdomain);
  const fallbackSubdomainName = isSubdomain && (activeTenant?.subdomain || (typeof window !== "undefined" ? window.location.hostname.split(".")[0] : ""))
    ? (activeTenant?.subdomain || window.location.hostname.split(".")[0]).charAt(0).toUpperCase() + (activeTenant?.subdomain || window.location.hostname.split(".")[0]).slice(1)
    : "";
  const activeTenantDisplayName =
    activeTenant?.name && activeTenant.name !== activeTenant.id && activeTenant.name !== "ClassNX"
      ? activeTenant.name
      : (activeTenant?.schoolName && activeTenant.schoolName !== "ClassNX" ? activeTenant.schoolName : "");
  const schoolName = isStudentRole
    ? studentProfile?.schoolName || ""
    : isTeacherRole
      ? teacherProfile?.schoolName || ""
      : isAdminRole
        ? activeTenantDisplayName
      : "";
  const displaySchoolName = schoolName
    || activeTenantDisplayName
    || (contextSchoolName && contextSchoolName !== "ClassNX" ? contextSchoolName : "")
    || (activeTenant?.schoolName && activeTenant.schoolName !== "ClassNX" ? activeTenant.schoolName : "")
    || fallbackSubdomainName
    || "";
  const superAdminCenterSchoolName = "";
  const normalizedPlan = String(
    activeTenant?.subscription?.plan || ""
  ).trim().toUpperCase();
  const subscriptionExpiry = new Date(activeTenant?.subscription?.renewalDate || "");
  const daysUntilExpiry = Number.isNaN(subscriptionExpiry.getTime())
    ? null
    : Math.ceil((subscriptionExpiry.getTime() - Date.now()) / 86400000);
  const isFreePlan = FREE_PLAN_SET.has(normalizedPlan);
  const isPremiumPlan = PREMIUM_PLAN_SET.has(normalizedPlan);
  const isExpiredOrNearExpiry = daysUntilExpiry !== null && daysUntilExpiry <= RENEWAL_NOTICE_DAYS;
  const hasVerifiedSubscription = subscriptionReady
    && !subscriptionLoading
    && !subscriptionError
    && Boolean(activeTenant?.subscription);
  const canManageSubscription = role === ROLE_ADMIN || role === ROLE_SCHOOL;
  const canPurchasePlan = hasVerifiedSubscription
    && canManageSubscription
    && (isFreePlan || isExpiredOrNearExpiry);
  const planActionLabel = isFreePlan ? "Upgrade plan" : "Renew plan";
  const whiteLabelEnabled = activeTenant?.whiteLabelEnabled === true
    || (isSubdomain && activeTenant?.brandingEnabled !== false);
  // Subdomain tenants are dedicated school domains and always show school branding
  const showWhiteLabelBranding = isSubdomain || whiteLabelEnabled;
  const showSchoolBranding = showWhiteLabelBranding && Boolean(displaySchoolName);
  const showLeftSchoolBranding = (isLoggedIn && !isSuperAdminRole) || (isSubdomain && !isSuperAdminRole);
  const isPasswordResetPage = ["/reset-password", "/set-password"].includes(location.pathname);
  const isManagementRole = MANAGEMENT_ROLES.includes(role) && role !== ROLE_SUPER_ADMIN;
  const dashboardPath = getDashboardPathForRole(role);
  const homePath = isSuperAdminRole ? "/super-admin/home" : isTeacherRole ? "/teacher/home" : isPrincipalRole ? "/principal" : isManagementRole ? "/admin-home" : "/";
  const showDashboardMenu = isLoggedIn && dashboardPath !== "/";
  const canSwitchTenant = role === ROLE_SUPER_ADMIN;
  const canManageBranding = role === ROLE_ADMIN || role === ROLE_SCHOOL;
  const settingsPath = isManagementRole && !isPrincipalRole ? "/admin-board" : "/profile";
  const showTenantContext = isManagementRole && !isAdminRole && !isSuperAdminRole && Boolean(activeTenant);
  const showPublicExploreLinks = !role && !isPasswordResetPage && !isSubdomain;
  const schoolInitials = displaySchoolName
    ? displaySchoolName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
    : "SC";
  const effectiveLogoUrl = tenantLogoUrl
    || privateLogoUrl
    || (activeTenant?.brandingEnabled !== false ? (activeTenant?.logoUrl || activeTenant?.schoolLogoUrl) : "");

  useEffect(() => {
    if (isSuperAdminRole || (!isLoggedIn && !isSubdomain) || !showWhiteLabelBranding) {
      if (privateLogoUrlRef.current) URL.revokeObjectURL(privateLogoUrlRef.current);
      privateLogoUrlRef.current = "";
      setPrivateLogoUrl("");
      return undefined;
    }

    let active = true;
    setPrivateLogoUrl("");
    const loadSchoolLogo = async () => {
      try {
        const response = await api.get(
          activeTenant?.schoolLogoUrl || API_PATHS.admin.schoolLogo(),
          { responseType: "blob", withCredentials: true }
        );
        if (!active || !response || !(response?.data instanceof Blob) || response?.data?.size === 0) return;
        const nextObjectUrl = URL.createObjectURL(response.data);
        if (privateLogoUrlRef.current) URL.revokeObjectURL(privateLogoUrlRef.current);
        privateLogoUrlRef.current = nextObjectUrl;
        setPrivateLogoUrl(nextObjectUrl);
      } catch (error) {
        const status = Number(error?.status || error?.response?.status || 0);
        const errorCode = String(
          error?.response?.data?.errorCode || error?.response?.data?.code || ""
        ).toUpperCase();
        const logoNotFound = status === 404 || errorCode === "DOCUMENT_NOT_FOUND";
        if (logoNotFound) {
          // A tenant without an uploaded private logo uses the default mark.
          if (privateLogoUrlRef.current) URL.revokeObjectURL(privateLogoUrlRef.current);
          privateLogoUrlRef.current = "";
          if (active) setPrivateLogoUrl("");
          return;
        }
        console.error("Unable to load the school logo:", error);
      }
    };
    const handleLogoUpdated = () => loadSchoolLogo();
    loadSchoolLogo();
    window.addEventListener("school-logo:updated", handleLogoUpdated);
    return () => {
      active = false;
      window.removeEventListener("school-logo:updated", handleLogoUpdated);
      if (privateLogoUrlRef.current) URL.revokeObjectURL(privateLogoUrlRef.current);
      privateLogoUrlRef.current = "";
    };
  }, [activeTenant?.id, activeTenant?.schoolLogoUrl, isLoggedIn, isSuperAdminRole, showWhiteLabelBranding, isSubdomain]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    const shouldLogout = window.confirm("Are you sure you want to logout?");
    if (!shouldLogout) return;

    setIsLoggingOut(true);
    window.dispatchEvent(new CustomEvent("auth:logout-progress", { detail: { pending: true } }));
    closeMenu();
    try {
      await revokeBrowserSession();
      terminateSession({
        reason: "manual",
        redirect: false,
        notify: false,
      });
      if (typeof setUserRole === "function") setUserRole(null);
      window.location.assign("/login");
    } catch (error) {
      // Keep local authentication intact when server revocation fails. This
      // prevents an active cookie session from reaching a misleading login UI.
      toast.error(error?.message || "Logout failed. Your session is still active; please try again.");
      setIsLoggingOut(false);
      window.dispatchEvent(new CustomEvent("auth:logout-progress", { detail: { pending: false } }));
    }
  };

  const closeMenu = () => { setIsOpen(false); setIsSettingsOpen(false); };
  const toggleMenu = () => setIsOpen((s) => !s);

  const openAdminDashboardModule = (path) => {
    const dispatchModuleSelect = () => {
      window.dispatchEvent(new CustomEvent("admin-dashboard:select-action", { detail: { path } }));
    };

    setIsSettingsOpen(false);
    setIsOpen(false);

    if (location.pathname !== "/admin-board") {
      navigate("/admin-board", { state: { adminModulePath: path } });
      return;
    }

    dispatchModuleSelect();
  };

  const handleOpenCustomizeFromHeader = () => {
    setIsSettingsOpen(false);
    if (location.pathname !== "/admin-board") {
      navigate("/admin-board");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("admin-dashboard:open-customize"));
      }, 150);
      return;
    }
    window.dispatchEvent(new CustomEvent("admin-dashboard:open-customize"));
  };

  const dismissDarkModeHint = () => {
    try {
      localStorage.setItem(DARK_MODE_HINT_KEY, "true");
    } catch {
      // Ignore storage failures; local UI state still hides the hint.
    }
    setShowDarkModeHint(false);
  };

  const handleLogoFileChange = (event) => {
    if (!isPremiumPlan) return;
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      setLogoDraftUrl(result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCustomLogo = () => {
    if (!isPremiumPlan || !logoDraftUrl) return;
    // A browser-only save would make a tenant-wide premium feature spoofable.
    // Persist this through a backend entitlement-checked branding endpoint.
    toast.error("Tenant branding must be saved through the secure server API.");
  };

  const handleRemoveCustomLogo = () => {
    setLogoDraftUrl("");
  };

  useEffect(() => {
    const handleClickOutsideSettings = (event) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) {
        setIsSettingsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideSettings);
    return () => document.removeEventListener("mousedown", handleClickOutsideSettings);
  }, []);

  useEffect(() => {
    if (!showLogoModal) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowLogoModal(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [showLogoModal]);

  useEffect(() => {
    setIsSettingsOpen(false);
  }, [location.pathname]);

  const isActive = (path, searchMatch = "") => {
    if (location.pathname !== path) return false;
    if (!searchMatch) return true;
    return location.search.includes(searchMatch);
  };
  const homeIsActive = isManagementRole
    ? location.pathname === homePath || location.pathname.startsWith("/admin/")
    : (isActive(homePath) || (homePath === "/" && location.pathname === "/home"));
  const aboutIsActive = isActive("/about");
  const dashboardMenuIsActive = showDashboardMenu && isActive(dashboardPath) && !isSettingsOpen;
  const profileMenuIsActive = isActive("/profile") && !isSettingsOpen;
  const mobilePageLabel = getMobilePageLabel(location.pathname);

  const navItemClass = (active) =>
    `theme-nav-item inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold select-none transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 ${active ? "theme-nav-item-active" : ""}`;

  const navIconItemClass = (active) => `${navItemClass(active)} h-9 w-9 justify-center gap-0 px-0`;
  const navHybridItemClass = (active) => `${navItemClass(active)} px-2.5 lg:px-3`;

  const navItemMobileClass = (active) =>
    `theme-mobile-nav-item ${active ? "theme-mobile-nav-item-active" : ""} flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold select-none transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70`;

  const headerShellClass = "theme-header-shell";

  const desktopNavClass = "theme-desktop-nav";

  const iconButtonClass = "theme-icon-button";

  const dropdownPanelClass = "theme-dropdown-panel";

  const dropdownItemClass = "theme-dropdown-item";

  const mobilePanelClass = "theme-mobile-panel";

  const tenantControlClass = "theme-tenant-control";

  return (
    <header className={`fixed left-0 right-0 top-0 z-50 shrink-0 backdrop-blur-xl ${isSuperAdminRole || isStudentRole || isTeacherRole || hasAdminSidebar ? "lg:left-[248px]" : ""} ${headerShellClass} ${isDarkMode ? "bg-slate-950/95" : ""}`}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-700 via-blue-500 to-teal-400" />
      <div className={`relative mx-auto flex items-center justify-between px-4 ${isSuperAdminRole || hasAdminSidebar ? "h-[4.5rem] max-w-none gap-3 py-0 lg:px-7" : "max-w-7xl py-3 md:py-3.5"}`}>
        {isLoggedIn && isSuperAdminRole && superAdminCenterSchoolName && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 hidden w-full max-w-[22rem] -translate-x-1/2 -translate-y-1/2 px-3 text-center lg:block xl:max-w-[28rem]">
            <span
              className={`block truncate text-sm font-black tracking-[0.08em] lg:text-base xl:text-lg ${
                isDarkMode ? "text-blue-200" : "text-blue-800"
              }`}
            >
              {superAdminCenterSchoolName}
            </span>
          </div>
        )}
        <div className="relative z-10 flex min-w-0 items-center gap-3">
          {showLeftSchoolBranding ? (
            <div className={`flex min-w-0 items-center gap-3 ${isManagementRole && !hasAdminSidebar ? "md:-ml-6 lg:-ml-10 xl:-ml-14" : ""}`}>
              <Link to={dashboardPath || homePath} className="group flex shrink-0 items-center gap-3">
                {showWhiteLabelBranding ? (effectiveLogoUrl ? (
                  <div className="theme-icon-button flex h-[3.35rem] w-[3.35rem] items-center justify-center overflow-hidden rounded-[1.4rem] transition duration-300 group-hover:-translate-y-0.5 md:h-14 md:w-14">
                    <img src={effectiveLogoUrl} alt={`${displaySchoolName || "School"} logo`} className="h-full w-full object-cover" />
                  </div>
                ) : (
                  <div className="theme-icon-button flex h-[3.35rem] w-[3.35rem] items-center justify-center rounded-[1.4rem] transition duration-300 group-hover:-translate-y-0.5 md:h-14 md:w-14">
                    <div className={`flex h-[2.45rem] w-[2.45rem] items-center justify-center rounded-[1rem] text-[0.8rem] font-semibold tracking-[0.18em] md:h-[2.65rem] md:w-[2.65rem] md:text-[0.84rem] ${isDarkMode ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"}`}>
                      {schoolInitials}
                    </div>
                  </div>
                )) : (
                  <img src={CLASSNX_LOGO_SRC} alt="ClassNX logo" className="h-[3.35rem] w-[3.35rem] rounded-[1.4rem] object-cover shadow-sm transition duration-300 group-hover:-translate-y-0.5 md:h-14 md:w-14" />
                )}
              </Link>
              <div className="min-w-0 leading-tight">
                <Link to={dashboardPath || homePath} className="group flex min-w-0 items-center gap-2.5">
                  <span className={`block truncate text-lg font-extrabold tracking-tight transition md:text-[1.65rem] ${
                    isDarkMode ? "text-white group-hover:text-emerald-300" : "text-slate-900 group-hover:text-emerald-700"
                  }`}>
                    {showWhiteLabelBranding ? (displaySchoolName || "Your school") : "ClassNX"}
                  </span>
                </Link>
                <span className={`mt-0.5 block truncate text-[10px] font-bold uppercase tracking-[0.14em] md:hidden ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  {mobilePageLabel}
                </span>
              </div>
            </div>
          ) : (
            <Link to={isSuperAdminRole ? "/super-admin/home" : "/"} className="group flex items-center gap-3">
              <img
                src={CLASSNX_LOGO_SRC}
                alt="ClassNX logo"
                className="h-10 w-10 rounded-full object-cover shadow-sm md:h-12 md:w-12"
              />
              <div className="leading-none">
                <span className="block text-xl font-black tracking-tight transition md:text-[1.65rem]">
                  <span className={isDarkMode ? "text-slate-100" : "text-[#11182f]"}>Class</span>
                  <span className="text-[#1684f4]">NX</span>
                </span>
                <small className={`hidden mt-1 block text-[7px] font-semibold uppercase tracking-[0.28em] md:text-[8px] ${
                  isDarkMode ? "text-slate-300" : "text-[#3f4656]"
                }`}>
                  Learn <span className="text-[#1668ea]">•</span> Grow <span className="text-[#c91dcc]">•</span> Excel
                </small>
                <small className={`mt-1 flex items-center gap-1.5 text-[7px] font-semibold uppercase tracking-[0.24em] md:gap-2 md:text-[8px] ${
                  isDarkMode ? "text-slate-300" : "text-[#4b5363]"
                }`}>
                  <span className="h-px w-4 bg-[#1684f4] md:w-5" />
                  <span>Learn</span>
                  <span className="text-[#1684f4]">&bull;</span>
                  <span>Grow</span>
                  <span className="text-[#1684f4]">&bull;</span>
                  <span>Excel</span>
                  <span className="h-px w-4 bg-[#1684f4] md:w-5" />
                </small>
                <span className={`mt-1 block max-w-[9rem] truncate text-[9px] font-bold uppercase tracking-[0.13em] md:hidden ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  {mobilePageLabel}
                </span>
              </div>
            </Link>
          )}
        </div>

        {/* Desktop nav */}
        <div className={`relative z-10 hidden shrink-0 items-center gap-3 md:flex ${isSuperAdminRole ? "super-admin-header-actions md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2" : ""}`}>
          {showTenantContext && (
            canSwitchTenant ? (
              <label className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${tenantControlClass}`}>
                <FaBuilding className={isDarkMode ? "text-blue-300" : "text-blue-600"} />
                <select
                  value={activeTenant?.id || ""}
                  onChange={(event) => setActiveTenantById(event.target.value)}
                  className={`bg-transparent text-sm font-semibold focus:outline-none ${isDarkMode ? "text-slate-100" : "text-slate-700"}`}
                  aria-label="Select active tenant"
                >
                  {tenantCatalog.map((tenant) => (
                    <option key={tenant.id} value={tenant.id} className="text-slate-900">
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <span className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${tenantControlClass}`}>
                <FaBuilding className={isDarkMode ? "text-blue-300" : "text-blue-600"} />
                {activeTenantDisplayName || "Tenant portal"}
              </span>
            )
          )}
          <nav className={`flex items-center gap-1.5 ${desktopNavClass} ${isSuperAdminRole ? "super-admin-header-nav" : ""}`}>
            <Link to={homePath} className={navHybridItemClass(homeIsActive)} title="Home" aria-label="Home">
              <FaHome />
              <span className="hidden lg:inline">Home</span>
            </Link>
            {showPublicExploreLinks && (
              <>
                <Link to="/services" className={navItemClass(isActive("/services"))}>
                  <FaBookOpen /> Services
                </Link>
                <Link to="/about" className={navItemClass(aboutIsActive)}>
                  <FaBuilding /> About
                </Link>
              </>
            )}
            {role && !isPrincipalRole && (
              <Link
                to="/moments"
                className={navHybridItemClass(isActive("/moments"))}
                title="Moments"
              >
                <FaHeart />
                <span className="hidden lg:inline">Moments</span>
              </Link>
            )}

            {!role ? (
              <Link to="/login" className={navItemClass(isActive("/login"))}>
                <FaUserCircle /> Login
              </Link>
            ) : (
              <>
                {showDashboardMenu && (
                  <Link
                    to={dashboardPath}
                    className={navHybridItemClass(dashboardMenuIsActive)}
                    title="Dashboard"
                    aria-label="Dashboard"
                  >
                    <FaTachometerAlt />
                    <span className="hidden lg:inline">Dashboard</span>
                  </Link>
                )}
                {canPurchasePlan && (
                  <Link
                    to="/subscription-plans"
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 px-3.5 py-2 text-sm font-bold text-white shadow-sm transition hover:from-blue-700 hover:to-cyan-700"
                    title="View and purchase subscription plans"
                  >
                    <FaCreditCard />
                    <span className="hidden lg:inline">{planActionLabel}</span>
                  </Link>
                )}
                <div ref={settingsMenuRef} className="relative">
                  <button
                    onClick={() => setIsSettingsOpen((current) => !current)}
                    className={navIconItemClass(isSettingsOpen)}
                    title="Settings"
                    aria-label="Settings"
                    aria-expanded={isSettingsOpen}
                    aria-haspopup="menu"
                  >
                    <FaCog />
                  </button>
                  <div
                    className={`absolute right-0 top-full z-20 mt-2 w-72 origin-top-right overflow-hidden rounded-xl backdrop-blur-xl transition-all duration-300 ${dropdownPanelClass} ${
                      isSettingsOpen
                        ? "translate-y-0 scale-100 opacity-100"
                        : "pointer-events-none -translate-y-1 scale-95 opacity-0"
                    }`}
                  >
                    <div className="max-h-[70vh] space-y-1 overflow-auto p-2">
                      <p className={`px-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                        Appearance
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          toggleTheme();
                          setIsSettingsOpen(false);
                        }}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${dropdownItemClass}`}
                      >
                        {isDarkMode ? <FaSun className="text-slate-500" /> : <FaMoon className="text-slate-400" />} {isDarkMode ? "Light Mode" : "Dark Mode"}
                      </button>
                      {canManageBranding && (
                        <button
                          type="button"
                          onClick={() => {
                            navigate("/settings/branding");
                            setIsSettingsOpen(false);
                          }}
                          disabled={!isPremiumPlan}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${dropdownItemClass} ${!isPremiumPlan ? "cursor-not-allowed opacity-55" : ""}`}
                        >
                          <FaImage className={isDarkMode ? "text-slate-500" : "text-slate-400"} /> Customize Logo {isPremiumPlan ? "" : "(Premium)"}
                        </button>
                      )}

                      <p className={`mt-2 px-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Help</p>
                      <Link to="/guide" onClick={() => setIsSettingsOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${dropdownItemClass}`}>
                        <FaCompass className={isDarkMode ? "text-slate-500" : "text-slate-400"} /> Product guide
                      </Link>

                      <p className={`mt-2 px-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                        Dashboard
                      </p>
                      {isManagementRole && (
                        <button
                          type="button"
                          onClick={handleOpenCustomizeFromHeader}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${dropdownItemClass}`}
                        >
                          <FaCog className={isDarkMode ? "text-slate-500" : "text-slate-400"} /> Customize Dashboard
                        </button>
                      )}

                      <p className={`mt-2 px-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                        Security
                      </p>
                      <Link
                        to="/profile"
                        state={{ message: "Use this form to reset your password." }}
                        onClick={() => setIsSettingsOpen(false)}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${dropdownItemClass}`}
                      >
                        <FaUserCircle className={isDarkMode ? "text-slate-500" : "text-slate-400"} /> Password Reset
                      </Link>
                    </div>
                  </div>
                </div>
                {isManagementRole ? (
                  <Link
                    to="/profile"
                    className={navIconItemClass(profileMenuIsActive)}
                    title="Profile"
                    aria-label="Profile"
                  >
                    <FaUserCircle />
                  </Link>
                ) : (
                  <Link
                    to="/profile"
                    className={navIconItemClass(profileMenuIsActive)}
                    title="Profile"
                    aria-label="Profile"
                  >
                    <FaUserCircle />
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className={`theme-danger-action relative inline-flex h-9 items-center justify-center gap-2 rounded-full px-3 transform-gpu transition-all duration-200 ease-out active:translate-y-[1px] active:scale-[0.95] disabled:cursor-wait disabled:opacity-70 ${isLoggingOut ? "min-w-[7.5rem]" : "w-9"}`}
                  aria-label={isLoggingOut ? "Logging out" : "Logout"}
                  title={isLoggingOut ? "Logging out" : "Logout"}
                >
                  {isLoggingOut ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" /><span className="text-xs font-semibold">Logging out...</span></> : <FaSignOutAlt />}
                </button>
              </>
            )}
          </nav>
          {/* Live session timer chip — students only */}
         
          {isManagementRole && (
            <button
              onClick={() => {
                if (isAdminRole) {
                  openAdminDashboardModule("/admin/notifications");
                  return;
                }
                navigate("/admin/notifications");
              }}
              className={`relative inline-flex h-9 w-9 items-center justify-center rounded-xl transform-gpu transition-all duration-200 ease-out active:translate-y-[1px] active:scale-[0.95] ${iconButtonClass}`}
              aria-label="Notifications"
            >
              <FaBell />
              {notifCount > 0 && (
                <span className="absolute -right-1 -top-1 rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-4 text-white">
                  {notifCount}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="flex items-center gap-2 md:hidden">
          {isManagementRole && (
            <button
              onClick={() => {
                if (isAdminRole) {
                  openAdminDashboardModule("/admin/notifications");
                  return;
                }
                navigate("/admin/notifications");
              }}
              className={`relative inline-flex h-9 w-9 items-center justify-center rounded-xl transform-gpu transition-all duration-200 ease-out active:translate-y-[1px] active:scale-[0.95] ${iconButtonClass}`}
              aria-label="Notifications"
            >
              <FaBell />
              {notifCount > 0 && (
                <span className="absolute -right-1 -top-1 rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-4 text-white">
                  {notifCount}
                </span>
              )}
            </button>
          )}
          <button
            onClick={toggleMenu}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-xl transform-gpu transition-all duration-200 ease-out active:translate-y-[1px] active:scale-[0.95] ${iconButtonClass}`}
          aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="classnx-mobile-navigation"
          >
            {isOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {showLogoModal && isPremiumPlan && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-slate-950/55 px-4 py-6 sm:py-10">
          <button type="button" className="fixed inset-0 cursor-default" onClick={() => setShowLogoModal(false)} aria-label="Close logo editor" />
          <div role="dialog" aria-modal="true" aria-labelledby="tenant-logo-title" className={`relative mx-auto w-full max-w-lg rounded-2xl border p-5 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <div className="flex items-center justify-between">
              <h3 id="tenant-logo-title" className="text-lg font-bold">Customize Tenant Logo</h3>
              <button
                type="button"
                onClick={() => setShowLogoModal(false)}
                className={`rounded-lg px-2 py-1 text-sm font-semibold ${isDarkMode ? "text-slate-400 hover:bg-slate-900 hover:text-slate-100" : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"}`}
                aria-label="Close logo modal"
              >
                <FaTimes />
              </button>
            </div>
            <p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
              This logo will be shown to all users of the tenant.
            </p>

            <div className={`mt-4 flex h-28 w-28 items-center justify-center overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-slate-50"}`}>
              {logoDraftUrl ? (
                <img src={logoDraftUrl} alt="Custom logo preview" className="h-full w-full object-cover" />
              ) : (
                <span className={`text-xs font-semibold uppercase tracking-[0.12em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                  No Logo
                </span>
              )}
            </div>

            <div className="mt-4 space-y-3">
              <label className="block">
                <span className={`mb-1 block text-xs font-semibold uppercase tracking-[0.14em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  Upload Image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  className={`w-full rounded-xl border px-3 py-2 text-sm ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-300 file:text-slate-200" : "border-slate-200 bg-white text-slate-700"}`}
                />
              </label>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleRemoveCustomLogo}
                className={`rounded-xl border px-3 py-2 text-sm font-semibold ${isDarkMode ? "border-rose-900/70 bg-rose-950/30 text-rose-300 hover:bg-rose-950/50" : "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"}`}
              >
                Remove
              </button>
              <button
                type="button"
                onClick={handleSaveCustomLogo}
                disabled={!logoDraftUrl}
                className={`rounded-xl px-4 py-2 text-sm font-semibold text-white ${logoDraftUrl ? "bg-gradient-to-r from-blue-700 to-teal-600" : "cursor-not-allowed bg-slate-400"}`}
              >
                Save Logo
              </button>
            </div>
          </div>
        </div>
      )}

      {showDarkModeHint && !isDarkMode && (
        <div className="border-t border-amber-200/80 bg-amber-50/90">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2 text-[13px] text-amber-900">
            <p className="font-medium">Use dark mode for better experience.</p>
            <button
              type="button"
              onClick={dismissDarkModeHint}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-800 transition hover:bg-amber-100"
              aria-label="Dismiss dark mode suggestion"
            >
              <FaTimes className="text-[10px]" />
              Dismiss
            </button>
          </div>
        </div>
      )}

      {isOpen && (
        <button
          type="button"
          className={`fixed inset-0 z-40 backdrop-blur-[2px] md:hidden ${isDarkMode ? "bg-slate-950/55" : "bg-slate-950/20"}`}
          onClick={closeMenu}
          aria-label="Close menu overlay"
        />
      )}

      {/* Mobile left drawer menu */}
      <div
        id="classnx-mobile-navigation"
        className={`fixed left-0 top-0 z-50 h-[100dvh] w-[86vw] max-w-[22rem] md:hidden ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <div
          className={`flex h-full flex-col overflow-hidden p-0 backdrop-blur-xl transition-transform duration-200 ${mobilePanelClass} ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className={`flex items-center justify-between gap-3 px-4 py-4 ${isDarkMode ? "bg-slate-950/90" : "bg-white/95"}`}>
            <div className="flex items-center gap-3">
              {showWhiteLabelBranding ? (effectiveLogoUrl ? (
                <img src={effectiveLogoUrl} alt={`${displaySchoolName || "School"} logo`} className="h-10 w-10 rounded-2xl object-cover" />
              ) : (
                <div className={`flex h-10 w-10 items-center justify-center rounded-2xl text-xs font-bold tracking-wider ${isDarkMode ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"}`}>
                  {schoolInitials}
                </div>
              )) : (
                <img src={CLASSNX_LOGO_SRC} alt="ClassNX" className="h-10 w-10 rounded-2xl object-cover" />
              )}
              <div>
                <p className={`text-sm font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{showWhiteLabelBranding ? (displaySchoolName || "Your school") : "ClassNX"}</p>
                <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Menu</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeMenu}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-lg transition hover:bg-slate-100/80 md:hidden"
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          </div>
          <nav
            className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-3 pb-4 pt-3"
            onClickCapture={(event) => {
              const clickableElement = event.target.closest("a,button");
              if (!clickableElement) return;
              closeMenu();
            }}
          >
            {showTenantContext && (
              <div className={`rounded-2xl px-4 py-3 ${tenantControlClass}`}>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  <FaBuilding className={isDarkMode ? "text-blue-300" : "text-blue-600"} />
                  Tenant Context
                </div>
                {canSwitchTenant ? (
                  <select
                    value={activeTenant?.id || ""}
                    onChange={(event) => {
                      setActiveTenantById(event.target.value);
                      closeMenu();
                    }}
                    className={`mt-3 w-full rounded-xl px-3 py-2 text-sm font-semibold ${tenantControlClass}`}
                    aria-label="Select active tenant"
                  >
                    {tenantCatalog.map((tenant) => (
                      <option key={tenant.id} value={tenant.id} className="text-slate-900">
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className={`mt-2 text-sm font-semibold ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                    {activeTenant?.name}
                  </p>
                )}
              </div>
            )}
            {!isSubdomain && showSchoolBranding && (
              <Link
                to="/about"
                className={navItemMobileClass(aboutIsActive)}
              >
                <FaBuilding className="text-blue-600" /> About ClassNX
              </Link>
            )}

            <Link to={homePath} className={navItemMobileClass(homeIsActive)}>
              <FaHome className={homeIsActive ? "text-white" : "text-blue-500"} /> Home
            </Link>

            {showPublicExploreLinks && (
              <>
                <Link to="/services" className={navItemMobileClass(isActive("/services"))}>
                  <FaBookOpen className={isActive("/services") ? "text-white" : "text-blue-500"} /> Services
                </Link>
                <Link to="/about" className={navItemMobileClass(aboutIsActive)}>
                  <FaBuilding className={aboutIsActive ? "text-white" : "text-blue-500"} /> About
                </Link>
              </>
            )}

            {role && !isPrincipalRole && (
              <Link
                to="/moments"
                className={navItemMobileClass(isActive("/moments"))}
              >
                <FaHeart className="text-rose-500" /> Moments
              </Link>
            )}

            {!role ? (
              <Link to="/login" className={navItemMobileClass(isActive("/login"))}>
                <FaUserCircle className={isActive("/login") ? "text-white" : "text-blue-500"} /> Login
              </Link>
            ) : (
              <>
                {showDashboardMenu && (
                  <Link
                    to={dashboardPath}
                    className={navItemMobileClass(isActive(dashboardPath))}
                  >
                    <FaTachometerAlt className={isActive(dashboardPath) ? "text-white" : "text-blue-500"} /> My Dashboard
                  </Link>
                )}
                {canPurchasePlan && (
                  <Link
                    to="/subscription-plans"
                    className={navItemMobileClass(isActive("/subscription-plans"))}
                  >
                    <FaCreditCard className={isActive("/subscription-plans") ? "text-white" : "text-emerald-500"} /> {planActionLabel}
                  </Link>
                )}
                <div className="theme-settings-section rounded-2xl px-4 py-3">
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    Settings
                  </p>
                  <div className="mt-2 space-y-2">
                    <p className={`px-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                      Appearance
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        toggleTheme();
                      }}
                      className={navItemMobileClass(false)}
                    >
                      {isDarkMode ? <FaSun className="text-amber-500" /> : <FaMoon className="text-amber-500" />} {isDarkMode ? "Light Mode" : "Dark Mode"}
                    </button>
                    {canManageBranding && (
                      <button
                        type="button"
                          onClick={() => {
                          navigate("/settings/branding");
                        }}
                        disabled={!isPremiumPlan}
                        className={`${navItemMobileClass(false)} ${!isPremiumPlan ? "cursor-not-allowed opacity-55" : ""}`}
                      >
                        <FaImage className="text-blue-500" /> Customize Logo {isPremiumPlan ? "" : "(Premium)"}
                      </button>
                    )}

                    <p className={`px-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                      Dashboard
                    </p>
                    {isManagementRole && (
                      <button
                        type="button"
                        onClick={() => {
                          handleOpenCustomizeFromHeader();
                        }}
                        className={navItemMobileClass(false)}
                      >
                        <FaCog className="text-blue-500" /> Customize Dashboard
                      </button>
                    )}

                    <p className={`px-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                      Security
                    </p>
                    <Link
                      to="/profile"
                      state={{ message: "Use this form to reset your password." }}
                      className={navItemMobileClass(isActive("/profile"))}
                    >
                      <FaUserCircle className={isActive("/profile") ? "text-white" : "text-blue-500"} /> Password Reset
                    </Link>
                  </div>
                </div>
                {isManagementRole ? (
                  <Link
                    to="/profile"
                    className={navItemMobileClass(isActive("/profile"))}
                  >
                    <FaUserCircle className={isActive("/profile") ? "text-white" : "text-blue-500"} /> Profile
                  </Link>
                ) : (
                  <Link to="/profile" className={navItemMobileClass(isActive("/profile"))}>
                    <FaUserCircle className={isActive("/profile") ? "text-white" : "text-blue-500"} /> Profile
                  </Link>
                )}
              </>
            )}
          </nav>
          {role && (
            <div className={`shrink-0 p-3 ${isDarkMode ? "bg-slate-950/90" : "bg-white/95"}`}>
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  closeMenu();
                }}
                disabled={isLoggingOut}
                className="theme-danger-action flex w-full items-center justify-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-all duration-200 active:scale-[0.98] disabled:cursor-wait disabled:opacity-70"
              >
                {isLoggingOut ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" /> Logging out...</> : <><FaSignOutAlt className="text-rose-500" /> Logout</>}
              </button>
            </div>
          )}
        </div>
      </div>
      {isLoggingOut && createPortal((
        <div
          ref={logoutOverlayRef}
          className={`fixed inset-0 z-[9999] flex items-center justify-center px-6 backdrop-blur-md ${isDarkMode ? "bg-slate-950/95" : "bg-gradient-to-br from-white/95 via-blue-50/95 to-cyan-50/95"}`}
          role="dialog"
          aria-modal="true"
          aria-label="Logging out"
          tabIndex={-1}
          onKeyDown={(event) => event.preventDefault()}
          onClick={(event) => event.preventDefault()}
        >
          <ClassNXLoader
            variant="page"
            size="lg"
            label="Closing your ClassNX workspace"
            detail="Securing your documents and logging you out..."
            className="min-h-0 bg-transparent border-0"
          />
        </div>
      ), document.body)}
    </header>
  );
};

export default Header;
